<script>
import { mapGetters } from 'vuex'
import ChargeResource from '@/resources/charge'
import PaymentMethod from '../components/PaymentMethod.vue'
import CostBreakdown from '../components/CostBreakdown.vue'
import UserEditForm from '../components/UserEditForm.vue'
import VoucherPitch from '../components/VoucherPitch.vue'
import TermsOfServiceLink from '../components/TermsOfServiceLink.vue'
import WaiverModal from '../components/WaiverModal.vue'
import UserUpcomingReservations from '../components/UserUpcomingReservations.vue'
import ReservationRacersForm from '../components/ReservationRacersForm.vue'
import ReservationSeatingGroupResource from '@/resources/reservationSeatingGroup'
import messages from '@/mixins/messages'

export default {
    name: 'Dashboard',
    mixins: [messages],
    components: {
        PaymentMethod,
        CostBreakdown,
        UserEditForm,
        VoucherPitch,
        TermsOfServiceLink,
        WaiverModal,
        UserUpcomingReservations,
        ReservationRacersForm,
    },
    computed: {
        ...mapGetters(['userResource', 'isApexClub', 'creditsBalance']),
        waiversOutstandingIds() {
            return this.$refs.waiverModal.waiversOutstanding.map((waiver) => waiver.id)
        },
        waiverApprovalsIds() {
            return this.$refs.waiverModal.waiverApprovals.map((approval) => approval.id)
        },
    },
    data() {
        return {
            loading: true,
            showPaymentMethod: true,
            chargeResources: [],
            currentTabIndex: 0,
            lapsPage: null,
            lapsNoneReorded: false,
            lapTimeQuery: '',
            lapsLoading: false,
            calendarResource: null,
            waiverResources: [],
            waiverViewing: null,
            optInChangesMade: false,
            optInAccount: null,
            optInMarketing: null,
            upcomingReservationsData: null,
            myRacerGroups: [],
            editingGroup: null,
            savingRacers: false,
        }
    },
    methods: {
        async load() {
            this.loading = true
            this.chargeResources = await ChargeResource.all({ query: { amount_cents__gt: 0 } })
            ;(this.optInAccount = this.userResource.attributes.opt_in_account_updates), (this.optInMarketing = this.userResource.attributes.opt_in_marketing_updates)
            this.loading = false

            this.$refs.waiverModal.$on('waivers', (waivers) => (this.waiverResources = waivers))
            this.loadRacerGroups()
        },
        async loadRacerGroups() {
            try {
                const groups = await ReservationSeatingGroupResource.mine()
                this.myRacerGroups = groups.filter((group) => String(group.attributes.created_by) === String(this.userResource.id))
            } catch (e) {
                this.myRacerGroups = []
            }
        },
        groupReservationIds(group) {
            return group.seats().map((seat) => seat.reservation)
        },
        openEditRacers(group) {
            this.editingGroup = group
            this.$refs.editRacersModal.show()
        },
        async saveEditRacers() {
            this.savingRacers = true
            const ok = this.$refs.editRacersForm ? await this.$refs.editRacersForm.run() : true
            this.savingRacers = false
            if (ok) {
                this.$refs.editRacersModal.hide()
                this.editingGroup = null
                await this.loadRacerGroups()
            }
        },
        async onUnenrollSubscription() {
            this.$refs.cancelSubscription.hide()
            await ChargeResource.creditUnenrollUser()
            this.userResource.set('credits_subscription_in_good_standing', false)
        },
        async onSaveNotifPrefs() {
            this.userResource.attributes.opt_in_account_updates = this.optInAccount
            this.userResource.attributes.opt_in_marketing_updates = this.optInMarketing
            await this.userResource.save()
            this.optInChangesMade = false
        },
        nthDayOfMonth(day) {
            const moment = require('moment')
            return moment(day, 'D').format('Do')
        },
    },
    async mounted() {
        await this.load()
    },
}
</script>

<template>
    <div>
        <header-image src="hero-main.png">
            <h1>Tableau de bord</h1>
        </header-image>

        <b-container class="mt-3 mb-5" v-if="!loading">
            <b-row>
                <b-col>
                    <user-upcoming-reservations poll-seconds="5" @reservations="(reservationData) => (upcomingReservationsData = reservationData)" />

                    <div v-if="$appConfigBool('APEX_CLUB_ENABLED', true) && !isApexClub">
                        <b-alert variant="info" class="mb-0" show
                            ><strong>Économisez 20 % sur chaque course</strong> en rejoignant <strong>{{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }}</strong
                            >. (Et bien plus encore !) <a href="javascript:{}" @click="$router.push({ name: 'subscription' })">Plus d’infos</a></b-alert
                        >
                    </div>

                    <b-card class="mb-3 mt-3" no-body>
                        <b-tabs class="scrollable-tabs-header" card>
                            <b-tab title="Profil">
                                <b-row>
                                    <b-col offset-md="2" md="8">
                                        <user-edit-form :user-resource="userResource" submit-text="Enregistrer le profil" success-message="Profil enregistré !" />
                                    </b-col>
                                </b-row>
                            </b-tab>

                            <b-tab title="Paiement">
                                <b-row>
                                    <b-col offset-md="3" md="6">
                                        <b-card>
                                            <payment-method cols-lg="12" :userResource="userResource" />
                                        </b-card>
                                    </b-col>
                                </b-row>
                            </b-tab>

                            <b-tab title="Abonnement" v-if="$appConfigBool('APEX_CLUB_ENABLED', true) && isApexClub">
                                <dl v-if="userResource.attributes.credits_subscription_in_good_standing" class="row mb-0">
                                    <dt class="col-sm-4">Apex Club</dt>
                                    <dd class="col-sm-8">
                                        <strong class="text-success"><b-icon icon="patch-check-fill" /> Membre</strong>
                                    </dd>
                                    <dt class="col-sm-4">Solde de crédits</dt>
                                    <dd class="col-sm-8">
                                        <strong><b-icon icon="currency-exchange" /> {{ creditsBalance }} crédits</strong>
                                    </dd>
                                    <dt class="col-sm-4">Abonnement</dt>
                                    <dd class="col-sm-8">
                                        <strong
                                            ><b-icon icon="arrow-repeat" /> {{ userResource.attributes.credits_recurring }} crédits par mois le {{ nthDayOfMonth(userResource.attributes.credits_cycle_day_of_month) }} — {{ currencySymbol()
                                            }}{{ moneyFormat(userResource.attributes.credits_recurring_cents) }}</strong
                                        >
                                    </dd>
                                    <dt class="col-sm-4">&nbsp;</dt>
                                    <dd class="col-sm-8">
                                        <small><a href="javascript:{};" class="text-muted" @click="$refs.cancelSubscription.show()">Résilier l’abonnement</a></small>
                                    </dd>
                                </dl>

                                <div v-else-if="!userResource.attributes.credits_subscription_in_good_standing && userResource.attributes.credits_subscription_exists">
                                    <b-alert variant="danger" class="mb-3" show
                                        ><strong>Oups !</strong> Vous avez un abonnement {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }} actif, mais votre compte n’est pas en règle. Veuillez <a href="javascript:{}" @click="currentTabIndex = 1">vérifier votre mode de paiement</a> pour résoudre ce problème.</b-alert
                                    >
                                </div>

                                <div v-else>
                                    <b-alert variant="info" class="mb-0" show
                                        ><strong>Économisez 20 % sur chaque course</strong> en rejoignant <strong>{{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }}</strong
                                        >. (Et bien plus encore !) <a href="javascript:{}" @click="$router.push({ name: 'subscription' })">Plus d’infos</a></b-alert
                                    >
                                </div>
                            </b-tab>

                            <b-tab title="Notifications">
                                <b-row>
                                    <b-col offset-md="2" md="8">
                                        <b-card>
                                            <small>
                                                <b-form-checkbox v-model="optInAccount" @input="optInChangesMade = true" switch>
                                                    En cochant cette case, j’accepte de recevoir des messages transactionnels concernant mon compte, mes commandes ou les services que j’ai demandés. Ces messages peuvent notamment inclure des rappels de rendez-vous, des confirmations de commande et des notifications liées au compte. La
                                                    fréquence des messages peut varier. Des frais de messagerie et de données peuvent s’appliquer. Répondez HELP pour obtenir de l’aide ou STOP pour vous désabonner.
                                                </b-form-checkbox>
                                                <b-form-checkbox v-model="optInMarketing" @input="optInChangesMade = true" class="mt-3" switch>
                                                    En cochant cette case, j’accepte de recevoir des messages marketing et promotionnels, notamment des offres spéciales, des réductions et des informations sur les nouveaux produits. La fréquence des messages peut varier. Des frais de messagerie et de données peuvent s’appliquer. Répondez HELP pour obtenir de l’aide ou STOP pour
                                                    vous désabonner.
                                                </b-form-checkbox>
                                            </small>
                                            <b-button variant="primary" :disabled="!optInChangesMade" @click="onSaveNotifPrefs()" class="mt-3">Enregistrer les notifications</b-button>
                                        </b-card>
                                    </b-col>
                                </b-row>
                            </b-tab>

                            <b-tab title="Réservations à venir">
                                <div v-if="upcomingReservationsData && upcomingReservationsData.future.length > 0">
                                    <b-table-simple class="nowrap-table" responsive>
                                        <b-thead>
                                            <b-th>Date</b-th>
                                            <b-th>Simulateur</b-th>
                                            <b-th>&nbsp;</b-th>
                                            <b-th>&nbsp;</b-th>
                                        </b-thead>
                                        <b-tbody>
                                            <b-tr v-for="(reserv, i) in upcomingReservationsData.future" :key="i">
                                                <b-td>{{ [reserv.getDateFormatted(), reserv.getTimeFormatted()].join(' à ') }}</b-td>
                                                <b-td>{{ reserv.attributes.calendar_name }}</b-td>
                                                <b-td
                                                    ><a :href="reserv.getAddToCalendarLink()"><b-icon icon="calendar-plus" /> Ajouter au calendrier</a></b-td
                                                >
                                                <b-td
                                                    ><b-link :to="{ name: 'calendarControlWaitingRoom', params: { calendarId: reserv.attributes.calendar } }"><b-icon icon="controller" /> Accéder au contrôleur du simulateur</b-link></b-td
                                                >
                                            </b-tr>
                                        </b-tbody>
                                    </b-table-simple>
                                </div>

                                <div v-else class="text-center pt-5 pb-5">
                                    <h3><b-icon icon="cone-striped" style="color: #ed8d34" /></h3>
                                    <p>Vous n’avez aucune réservation à venir</p>
                                    <b-button variant="secondary" :to="{ name: 'reservations' }">Voir les disponibilités</b-button>
                                </div>
                            </b-tab>

                            <b-tab title="Vos pilotes" v-if="myRacerGroups.length > 0">
                                <div v-for="group in myRacerGroups" :key="group.id" class="mb-4">
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <strong>{{ group.attributes.created ? new Date(group.attributes.created).toLocaleDateString() : 'Réservation' }} &mdash; {{ group.seats().length }} simulateurs</strong>
                                        <b-button size="sm" variant="secondary" @click="openEditRacers(group)">Ajouter/modifier des pilotes</b-button>
                                    </div>
                                    <b-list-group>
                                        <b-list-group-item v-for="seat in group.seats()" :key="seat.reservation" class="d-flex justify-content-between">
                                            <span
                                                >{{ seat.calendar_name }} <span class="text-muted small">{{ seat.time ? new Date(seat.time).toLocaleString() : '' }}</span></span
                                            >
                                            <span>{{ seat.seated_user_display_name || 'Libre' }}</span>
                                        </b-list-group-item>
                                    </b-list-group>
                                </div>
                            </b-tab>

                            <b-tab title="Décharges">
                                <b-table-simple class="nowrap-table" responsive v-if="waiverResources.length > 0">
                                    <b-thead>
                                        <b-th>&nbsp;</b-th>
                                        <b-th>Statut</b-th>
                                        <b-th>Nom de la décharge</b-th>
                                    </b-thead>
                                    <b-tbody>
                                        <b-tr v-for="waiver in waiverResources" :key="waiver.id">
                                            <b-td class="text-right">
                                                <b-button variant="danger" size="sm" v-if="waiversOutstandingIds.includes(waiver.id)" @click="waiverViewing = waiver">Signer</b-button>
                                                <b-button variant="outline-primary" size="sm" v-else @click="waiverViewing = waiver">Voir</b-button>
                                            </b-td>
                                            <b-td>{{ waiver.attributes.required ? 'Obligatoire' : 'Facultative' }}</b-td>
                                            <b-td :class="{ 'font-weight-bold': waiver.attributes.required }">{{ waiver }}</b-td>
                                        </b-tr>
                                    </b-tbody>
                                </b-table-simple>

                                <p v-else class="text-center">Aucune décharge à signer.</p>
                            </b-tab>

                            <b-tab title="Temps au tour" class="text-center">
                                <b-button-group>
                                    <b-button variant="outline-primary" :to="{ name: 'laps' }"><b-icon icon="stopwatch-fill" /> Accéder à MesTours</b-button>
                                    <b-button variant="outline-primary" :to="{ name: 'leaderboards' }"><b-icon icon="table" /> Accéder aux classements</b-button>
                                </b-button-group>
                            </b-tab>

                            <b-tab title="Préréglages de course" class="text-center">
                                <p>Préparez votre course à votre façon. <strong>Personnalisez votre voiture, le circuit, les aides et les paramètres de session</strong> avant de prendre place sur la grille. Que vous souhaitiez une balade décontractée ou un défi compétitif, ajustez votre expérience à tout moment avant le début de la course.</p>
                                <b-button variant="outline-primary" :to="{ name: 'racesPresets' }"><b-icon icon="collection-play" /> Personnaliser votre course</b-button>
                            </b-tab>

                            <b-tab title="Transactions récentes">
                                <b-table-simple v-if="chargeResources.length > 0" class="nowrap-table mt-2" responsive>
                                    <b-thead>
                                        <b-th>Montant</b-th>
                                        <b-th>Description</b-th>
                                        <b-th>Reçu</b-th>
                                    </b-thead>
                                    <b-tbody>
                                        <b-tr v-for="(chargeResource, i) in chargeResources" :key="i">
                                            <b-td>{{ currencySymbol() }}{{ chargeResource.amount() }}</b-td>
                                            <b-td
                                                >{{ chargeResource.get('notes') || 'Aucune description' }}<br /><small class="text-muted"
                                                    >{{ chargeResource.createdTimePretty() }} <span class="text-danger" v-if="chargeResource.get('errors')"><br />{{ chargeResource.get('errors') }}</span></small
                                                ></b-td
                                            >
                                            <b-td>
                                                <a :href="chargeResource.get('receipt_url')" target="_blank" v-if="chargeResource.attributes.receipt_url">Voir</a>
                                                <span v-else class="text-muted">&nbsp;</span>
                                            </b-td>
                                        </b-tr>
                                    </b-tbody>
                                </b-table-simple>
                                <p v-else class="text-center">Aucune transaction récente</p>
                            </b-tab>
                        </b-tabs>
                    </b-card>
                </b-col>
            </b-row>
        </b-container>

        <b-modal
            title="Résilier l’abonnement"
            ref="cancelSubscription"
            @ok="onUnenrollSubscription()"
            v-if="userResource"
            :ok-disabled="!userResource.attributes.credits_subscription_cancelable"
            cancel-title="Ne pas résilier"
            cancel-variant="primary"
            ok-title="Résilier"
            ok-variant="danger"
            centered
            no-fade
        >
            <p>
                Voulez-vous vraiment résilier votre abonnement ? Cette action est irréversible.
                <span v-if="creditsBalance > 0"
                    ><strong>Vous perdrez votre solde de {{ creditsBalance }} crédits !</strong> Pensez à utiliser vos crédits pour effectuer une réservation avant de résilier.</span
                >
            </p>
            <b-alert variant="danger" :show="!userResource.attributes.credits_subscription_cancelable">
                <strong>Désolé ! Vous ne pouvez pas encore résilier votre abonnement</strong> car vous devez rester membre pendant au moins deux cycles de facturation. Veuillez consulter nos <terms-of-service-link text="Conditions d’utilisation" el="span" />.
            </b-alert>
        </b-modal>

        <waiver-modal ref="waiverModal" v-model="waiverViewing" />

        <b-modal ref="editRacersModal" title="Vos pilotes" no-fade no-enforce-focus centered @ok.prevent="saveEditRacers" ok-title="Enregistrer" cancel-title="Fermer" :ok-disabled="savingRacers">
            <reservation-racers-form v-if="editingGroup" ref="editRacersForm" :reservation-ids="groupReservationIds(editingGroup)" :purchaser="userResource" :existing-group="editingGroup" />
        </b-modal>
    </div>
</template>

<style lang="scss">
.add-card {
    padding-top: 10px;
}

.b-icon.gold {
    color: #ecbd00;
}

.lap-times-table {
    overflow: scroll;

    table {
        min-width: 900px;
    }
}

.tabs.scrollable-tabs-header {
    .card-header {
        overflow-x: scroll;
        overflow-y: hidden;
        height: 55px;

        ul.card-header-tabs {
            width: 1200px;
        }
    }
}

.nowrap-table {
    td,
    th {
        white-space: nowrap;
    }
}
</style>
